import React, { useRef } from 'react';
import { SectionReveal } from './ui/SectionReveal';
import { GraduationCap, ShoppingCart, Plane, Umbrella, Navigation, HeartPulse, Dumbbell, Dog, Theater, Store, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
type PlaceKey = 'school' | 'mall' | 'airport' | 'cristianos' | 'adeje' | 'golf' | 'medical' | 'gym' | 'dogPark' | 'culture' | 'sports' | 'supermarket';
const locations: Array<{
  icon: typeof GraduationCap;
  key: PlaceKey;
  distance: string;
}> = [{
  icon: GraduationCap,
  key: 'school',
  distance: '3 min'
}, {
  icon: ShoppingCart,
  key: 'mall',
  distance: '2 min'
}, {
  icon: Plane,
  key: 'airport',
  distance: '10 min'
}, {
  icon: Umbrella,
  key: 'cristianos',
  distance: '10 min'
}, {
  icon: Navigation,
  key: 'adeje',
  distance: '15 min'
}, {
  icon: Navigation,
  key: 'golf',
  distance: '8 min'
}, {
  icon: HeartPulse,
  key: 'medical',
  distance: '2 min'
}, {
  icon: Dumbbell,
  key: 'gym',
  distance: '2 min'
}, {
  icon: Dog,
  key: 'dogPark',
  distance: '2 min'
}, {
  icon: Theater,
  key: 'culture',
  distance: '2 min'
}, {
  icon: Dumbbell,
  key: 'sports',
  distance: '2 min'
}, {
  icon: Store,
  key: 'supermarket',
  distance: '2 min'
}];
export function LocationSection() {
  const {
    t
  } = useLanguage();
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollBy = (direction: 'prev' | 'next') => {
    const track = trackRef.current;
    if (!track) return;
    const amount = track.clientWidth * 0.8;
    track.scrollBy({
      left: direction === 'next' ? amount : -amount,
      behavior: 'smooth'
    });
  };
  return <SectionReveal id="entorno" className="relative bg-navy overflow-hidden" data-id="element-245">
      {/* Background photo */}
      <div className="absolute inset-0 z-0" data-id="element-246">
        <img src="https://cdn.magicpatterns.com/uploads/vKHt2JcAVQoybVVAaMgBg5/ChatGPT_Image_8_jun_2026,_16_23_53.png" alt="Vista del residencial Aneto" className="w-full h-full object-cover" data-id="element-247" />
        <div className="absolute inset-0 bg-navy/85" data-id="element-248" />
      </div>

      <div className="container relative z-10 mx-auto px-6 md:px-12" data-id="element-249">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-250">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6" data-id="element-251">
            {t.location.title}
          </h2>
          <p className="text-xl text-white/80 font-medium italic" data-id="element-252">
            {t.location.subtitle}
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto px-0 md:px-14" data-id="element-253">
          {/* Prev arrow */}
          <button type="button" onClick={() => scrollBy('prev')} aria-label="Anterior" className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-11 h-11 rounded-full bg-white text-navy shadow-lg hover:bg-sand-light transition-colors" data-id="element-254">
            <ChevronLeft className="w-5 h-5" data-id="element-255" />
          </button>

          {/* Track */}
          <div ref={trackRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-id="element-256">
            {locations.map((loc, index) => <div key={index} className="snap-start shrink-0 w-[80%] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)] flex items-center gap-4 p-4 rounded-xl transition-colors border border-white/10 bg-navy/30 backdrop-blur-sm hover:bg-white/10" data-id="element-257">
                <div className="bg-white/10 p-3 rounded-lg text-sand" data-id="element-258">
                  <loc.icon className="w-5 h-5" data-id="element-259" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-260">
                  <h4 className="font-medium text-white text-sm truncate" data-id="element-261">
                    {t.location.places[loc.key]}
                  </h4>
                </div>
                <span className="text-sm font-semibold text-sand whitespace-nowrap" data-id="element-262">
                  {loc.distance}
                </span>
              </div>)}
          </div>

          {/* Next arrow */}
          <button type="button" onClick={() => scrollBy('next')} aria-label="Siguiente" className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-11 h-11 rounded-full bg-white text-navy shadow-lg hover:bg-sand-light transition-colors" data-id="element-263">
            <ChevronRight className="w-5 h-5" data-id="element-264" />
          </button>

          {/* Mobile arrows */}
          <div className="flex md:hidden justify-center gap-4 mt-6" data-id="element-265">
            <button type="button" onClick={() => scrollBy('prev')} aria-label="Anterior" className="flex items-center justify-center w-11 h-11 rounded-full bg-white text-navy shadow-lg hover:bg-sand-light transition-colors" data-id="element-266">
              <ChevronLeft className="w-5 h-5" data-id="element-267" />
            </button>
            <button type="button" onClick={() => scrollBy('next')} aria-label="Siguiente" className="flex items-center justify-center w-11 h-11 rounded-full bg-white text-navy shadow-lg hover:bg-sand-light transition-colors" data-id="element-268">
              <ChevronRight className="w-5 h-5" data-id="element-269" />
            </button>
          </div>
        </div>
      </div>
    </SectionReveal>;
}